import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getIsAuthenticated } from "@/src/auth/store/auth-selectors";
import { LegalFooter } from "@/src/common/components/legal-footer";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Spinner } from "@/src/common/components/ui";
import CommunityNotFoundPage from "@/src/common/pages/community-not-found-page";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { useGetPublicCommunityMobileAppConfigQuery, useGetPublicCommunityQuery } from "@/src/common/services/community-public-http.service";
import { setVariant } from "@/src/common/store/general-slice";
import { LandingAccountDeletionSection } from "@/src/community/components/landing/sections/landing-account-deletion-section";
import { LandingAppSection } from "@/src/community/components/landing/sections/landing-app-section";
import { LandingFaqSection } from "@/src/community/components/landing/sections/landing-faq-section";
import { LandingFeaturesSection } from "@/src/community/components/landing/sections/landing-features-section";
import { LandingFinalCtaSection } from "@/src/community/components/landing/sections/landing-final-cta-section";
import { LandingGallerySection } from "@/src/community/components/landing/sections/landing-gallery-section";
import { LandingHeroSection } from "@/src/community/components/landing/sections/landing-hero-section";
import { LandingTestimonialsSection } from "@/src/community/components/landing/sections/landing-testimonials-section";
import { PublicCommunityHeader } from "@/src/community/components/public-community-header";
import "@/src/community/styles/landing.css";

interface PublicCommunityHomePageProps {
	slug: string;
}

export default function PublicCommunityHomePage({ slug }: PublicCommunityHomePageProps) {
	const { t } = useTranslation("community");
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(getIsAuthenticated);

	const { data: community, isLoading: communityLoading, isError: communityError } = useGetPublicCommunityQuery(slug);
	const { data: mobileApp, isLoading: mobileAppLoading } = useGetPublicCommunityMobileAppConfigQuery(slug);

	const colorPrimary = mobileApp?.color_primary ?? null;
	const colorSecondary = mobileApp?.color_secondary ?? null;
	const colorSplash = mobileApp?.color_splash ?? null;
	const iconUrl = mobileApp?.icon_url ?? null;
	const appName = mobileApp?.app_name ?? null;

	useEffect(() => {
		if (!colorPrimary && !colorSecondary) return;

		const root = document.documentElement;
		const prevPrimary = root.style.getPropertyValue("--color-primary");
		const prevSecondary = root.style.getPropertyValue("--color-secondary");

		if (colorPrimary) root.style.setProperty("--color-primary", colorPrimary);
		if (colorSecondary) root.style.setProperty("--color-secondary", colorSecondary);

		return () => {
			if (colorPrimary) {
				if (prevPrimary) root.style.setProperty("--color-primary", prevPrimary);
				else root.style.removeProperty("--color-primary");
			}
			if (colorSecondary) {
				if (prevSecondary) root.style.setProperty("--color-secondary", prevSecondary);
				else root.style.removeProperty("--color-secondary");
			}
		};
	}, [colorPrimary, colorSecondary]);

	useEffect(() => {
		if (community && !mobileApp?.color_primary) {
			dispatch(setVariant(community.variant));
		}
	}, [community, mobileApp?.color_primary, dispatch]);

	if (communityLoading || mobileAppLoading) {
		return (
			<>
				<PageHelmet title={t("home_loading")} />
				<div className="flex min-h-screen items-center justify-center">
					<Spinner />
				</div>
			</>
		);
	}

	if (communityError || !community) {
		return <CommunityNotFoundPage />;
	}

	return (
		<>
			<PageHelmet
				title={community.name}
				ogTitle={community.name}
				ogDescription={community.description ?? undefined}
				ogImage={community.image ?? null}
				communityName={community.name}
				communityImage={community.image ?? null}
			/>

			<div className="flex min-h-screen flex-col bg-background">
				<PublicCommunityHeader communityName={community.name} communityImage={community.image ?? null} />

				<main className="flex-1">
					<LandingHeroSection
						communityName={community.name}
						communityDescription={community.description ?? null}
						appName={appName}
						iconUrl={iconUrl}
						splashColor={colorSplash}
						primaryColor={colorPrimary}
						isAuthenticated={isAuthenticated}
					/>

					<LandingFeaturesSection communityName={community.name} features={community.features} />

					<LandingGallerySection communityName={community.name} coverImages={community.cover_images} />

					<LandingTestimonialsSection testimonials={community.testimonials} />

					<LandingFaqSection communityName={community.name} />

					<LandingAppSection
						communityName={community.name}
						appName={appName}
						iconUrl={iconUrl}
						splashColor={colorSplash}
						primaryColor={colorPrimary}
					/>

					<LandingAccountDeletionSection communityName={community.name} />

					<LandingFinalCtaSection communityName={community.name} isAuthenticated={isAuthenticated} />
				</main>

				<LegalFooter communityName={community.name} />
			</div>
		</>
	);
}
